import moment from "moment";
import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Airport, FlightSearchData } from "../../../@types";
import { AirplaneInFlight } from "@phosphor-icons/react";
import { formatAsMoney } from "../../../helpers/numbers";
import { CheckCircle } from "@phosphor-icons/react/dist/ssr";

type ResultsData = {
  data: FlightSearchData | null;
  departureDate: string;
  journeyType: string;
  class: string;
  returnDate: string;
  airportOriginCode: string;
  airlineCode: string;
  airportDestinationCode: string;
  infants: number;
  adults: number;
  children: number;
  [key: string]: any;
  airport: Airport;
};

const SearchResults = () => {
  const { state } = useLocation();

  const [selectedAirlineFilter, setSelectedAirlineFilter] = React.useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (state?.searchResults) {
      sessionStorage.setItem(
        "search-flights-result",
        JSON.stringify(state.searchResults)
      );
    }
  }, [state?.searchResults]);

  const searchResult: ResultsData = useMemo(() => {
    if (state?.searchResults) {
      return state.searchResults as ResultsData;
    }

    const stored = sessionStorage.getItem("search-flights-result");
    return stored ? (JSON.parse(stored) as ResultsData) : ({} as ResultsData);
  }, [state?.searchResults]);

  const allAirlinesAvailable = useMemo(() => {
    const airlines =
      searchResult?.data?.AirSearchResponse?.AirSearchResult?.FareItineraries?.map(
        (fare) =>
          fare.FareItinerary.OriginDestinationOptions.at(
            0
          )?.OriginDestinationOption.at(0)?.FlightSegment?.MarketingAirlineName
      ) || [];
    return Array.from(new Set(airlines.filter(Boolean)));
  }, [searchResult?.data?.AirSearchResponse?.AirSearchResult?.FareItineraries]);

  return searchResult?.data ? (
    <div className="p-5 lg:p-10 py-10 flex flex-col gap-10">
      <div>
        <div className="flex flex-col gap">
          <h1 className="text-2xl font-medium">Available Flights</h1>
          <div className="text-lg font-medium text-visaro-orange">
            {moment(searchResult.departureDate).format("MMMM Do YYYY")}
          </div>
          <div className="text-gray-700">
            10 flights available on{" "}
            {moment(searchResult.departureDate).format("MMMM Do YYYY")}
          </div>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap text-gray-500 w-full items-center">
        <span>Available Airlines:</span>
        {allAirlinesAvailable?.map((airline, index) => (
          <span
            data-testid={`airline-filter-${airline}`}
            data-selected={selectedAirlineFilter === airline ? true : "false"}
            onClick={() => {
              setSelectedAirlineFilter((prev) => {
                if (prev === airline) {
                  return undefined; // Deselect if already selected
                }
                return airline; // Select the clicked airline
              });
            }}
            key={index}
            className="px-3 py-1 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200 transition-colors duration-300 hover:text-visaro-orange data-[selected='true']:text-visaro-orange data-[selected='true']:bg-gray-200 data-[selected='true']:font-medium text-sm flex items-center gap-2"
          >
            <span>{airline}</span>
            {selectedAirlineFilter === airline ? (
              <CheckCircle
                weight="fill"
                className="size-4 text-visaro-orange"
              />
            ) : null}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:grid-cols-3">
        {searchResult?.data?.AirSearchResponse?.AirSearchResult?.FareItineraries?.filter(
          (fare) =>
            !selectedAirlineFilter ||
            fare.FareItinerary.OriginDestinationOptions.at(
              0
            )?.OriginDestinationOption.at(0)?.FlightSegment
              ?.MarketingAirlineName === selectedAirlineFilter
        ).map((fare) => {
          return (
            <div
              key={fare.FareItinerary.AirItineraryFareInfo.FareSourceCode}
              className="flex flex-col gap-4 rounded-md border border-gray-200 p-5 items-stretch hover:bg-gray-50 transition-all duration-300 hover:border-gray-500"
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-col items-start">
                  <div className="text-lg font-medium">
                    {
                      fare.FareItinerary.OriginDestinationOptions.at(
                        0
                      )?.OriginDestinationOption.at(0)?.FlightSegment
                        ?.DepartureAirportLocationCode
                    }
                  </div>
                  <div className="text-gray-500 text-sm">
                    {moment(
                      fare.FareItinerary.OriginDestinationOptions.at(
                        0
                      )?.OriginDestinationOption.at(0)?.FlightSegment
                        ?.DepartureDateTime
                    ).format("MMM DD, YYYY - hh:mm A")}
                  </div>
                </div>
                <div className="flex-1 flex gap-4 items-center">
                  <hr className="flex-1" />
                  <div className="flex items-center gap-2 rounded-full bg-visaro-primary-700/5 text-visaro-primary-700 p-4">
                    <AirplaneInFlight className="size-6 md:size-8" />
                  </div>
                  <hr className="flex-1" />
                </div>
                <div className="flex flex-col items-end">
                  <div className="text-lg font-medium">
                    {
                      fare.FareItinerary.OriginDestinationOptions.at(
                        0
                      )?.OriginDestinationOption.at(
                        fare?.FareItinerary?.OriginDestinationOptions?.at(0)
                          ?.OriginDestinationOption
                          ? fare.FareItinerary.OriginDestinationOptions.at(0)!
                              .OriginDestinationOption.length - 1
                          : 0
                      )?.FlightSegment?.ArrivalAirportLocationCode
                    }
                  </div>
                  <div className="text-gray-500 text-sm">
                    {moment(
                      fare.FareItinerary.OriginDestinationOptions.at(
                        0
                      )?.OriginDestinationOption.at(
                        fare?.FareItinerary?.OriginDestinationOptions?.at(0)
                          ?.OriginDestinationOption
                          ? fare.FareItinerary.OriginDestinationOptions.at(0)!
                              .OriginDestinationOption.length - 1
                          : 0
                      )?.FlightSegment?.ArrivalDateTime
                    ).format("MMM DD, YYYY - hh:mm A")}
                  </div>
                </div>
              </div>
              <div className="text-visaro-orange">
                {
                  fare.FareItinerary.OriginDestinationOptions.at(
                    0
                  )?.OriginDestinationOption.at(0)?.FlightSegment
                    ?.MarketingAirlineName
                }
              </div>
              <div className="flex items-center gap-2">
                <div className="text-sm p-1 px-2 bg-visaro-primary-700/10 text-visaro-primary-700 rounded-md w-fit capitalize font-medium">
                  {fare.FareItinerary.DirectionInd}
                </div>
                <div className="text-sm p-1 px-2 bg-visaro-gray-700/10 text-visaro-gray-700 rounded-md w-fit capitalize font-medium">
                  {fare.FareItinerary.AirItineraryFareInfo.FareType}
                </div>
                <div className="text-sm p-1 px-2 bg-visaro-gray-700/10 text-visaro-gray-700 rounded-md w-fit font-medium">
                  {
                    fare.FareItinerary.OriginDestinationOptions.at(0)
                      ?.TotalStops
                  }{" "}
                  Stop Over
                  {(fare.FareItinerary.OriginDestinationOptions.at(0)
                    ?.TotalStops || 0) > 1 && "s"}
                </div>
                <div className="flex-1"></div>
                <div className="font-medium text-xl">
                  {
                    fare.FareItinerary.AirItineraryFareInfo.ItinTotalFares
                      .TotalFare.CurrencyCode
                  }{" "}
                  {formatAsMoney(
                    fare.FareItinerary.AirItineraryFareInfo.ItinTotalFares
                      .TotalFare.Amount
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ) : (
    <div>
      <div className="p-5 lg:p-10 py-10 flex flex-col gap-10">
        <h1 className="text-2xl font-medium">No Flights Found</h1>
        <p className="text-gray-700">Please try a different search.</p>
      </div>
    </div>
  );
};

export default SearchResults;
